import { Component, Input, OnChanges } from '@angular/core';

/**
 * Replaces <canvas mdbChart> (MDB's Chart.js 2 wrapper) with DevExtreme charts.
 * Takes the same Chart.js-shaped inputs the dashboards already build
 * (chartType, labels, datasets[{label, data}], colors[{backgroundColor}]) and maps them
 * the same way frontend's shared-dashboard does. Data is rebuilt only when inputs change,
 * so change detection doesn't hand DevExtreme a new array (and a re-render) every pass.
 */
@Component({
    selector: 'app-chart',
    template: `
    <ng-container [ngSwitch]="kind">
      <dx-pie-chart *ngSwitchCase="'pie'" [type]="chartType === 'doughnut' ? 'doughnut' : 'pie'"
        [dataSource]="pieData" [palette]="pieColors">
        <dxi-series argumentField="arg" valueField="val"><dxo-label [visible]="false"></dxo-label></dxi-series>
        <dxo-legend [visible]="legend" verticalAlignment="bottom" horizontalAlignment="center"></dxo-legend>
      </dx-pie-chart>
      <dx-polar-chart *ngSwitchCase="'radar'" [dataSource]="seriesData" [palette]="seriesColors">
        <dxi-series *ngFor="let s of series" argumentField="label" [valueField]="s.valueField" [name]="s.name" type="line"></dxi-series>
        <dxo-legend [visible]="legend" verticalAlignment="bottom" horizontalAlignment="center"></dxo-legend>
      </dx-polar-chart>
      <dx-chart *ngSwitchDefault [dataSource]="seriesData" [rotated]="chartType === 'horizontalBar'" [palette]="seriesColors">
        <dxi-series *ngFor="let s of series" argumentField="label" [valueField]="s.valueField" [name]="s.name"
          [type]="chartType === 'line' ? 'line' : 'bar'"></dxi-series>
        <dxo-legend [visible]="legend" verticalAlignment="bottom" horizontalAlignment="center"></dxo-legend>
      </dx-chart>
    </ng-container>`,
    standalone: false
})
export class ChartComponent implements OnChanges {
  @Input() chartType: string;
  @Input() labels: string[] = [];
  @Input() datasets: any[] = [];
  @Input() colors: any[] = [];
  @Input() legend = true;

  kind: 'pie' | 'radar' | 'xy' = 'xy';
  pieData: any[] = [];
  pieColors: string[] | string = 'Material';
  seriesData: any[] = [];
  series: { valueField: string; name: string }[] = [];
  seriesColors: string[] | string = 'Material';

  ngOnChanges() {
    const labels = this.labels || [];
    const datasets = this.datasets || [];
    this.kind = this.chartType === 'pie' || this.chartType === 'doughnut' ? 'pie' : this.chartType === 'radar' ? 'radar' : 'xy';

    // pie/doughnut: one dataset, one color per slice
    this.pieData = labels.map((label, i) => ({ arg: label, val: +(datasets[0]?.data?.[i] ?? 0) }));
    const bg = this.colors?.[0]?.backgroundColor;
    this.pieColors = Array.isArray(bg) && bg.length ? bg : 'Material';

    // bar/line/radar: one series per dataset, one color per series
    this.series = datasets.map((ds, i) => ({ valueField: 's' + i, name: ds.label || 'Series ' + (i + 1) }));
    this.seriesData = labels.map((label, i) => {
      const point: any = { label };
      datasets.forEach((ds, idx) => (point['s' + idx] = +(ds.data?.[i] ?? 0)));
      return point;
    });
    const sc = datasets.map((ds, i) => {
      const c = this.colors?.[i]?.backgroundColor ?? ds.backgroundColor;
      return Array.isArray(c) ? c[0] : c;
    }).filter(Boolean);
    this.seriesColors = sc.length ? sc : 'Material';
  }
}
